<template>
  <div class="document-templates-container">
    <div class="header-section">
      <h1 class="page-title">{{ $t("common.document_templates") }}</h1>

      <!-- Search Bar -->
      <div class="search-section">
        <v-text-field
          v-model="searchQuery"
          :placeholder="$t('common.search_bar_placeholder')"
          variant="outlined"
          density="comfortable"
          prepend-inner-icon="mdi-magnify"
          clearable
          class="search-input"
          @input="handleSearch"
        />
      </div>

      <!-- Category Filters -->
      <div class="filter-section">
        <v-chip-group
          v-model="selectedCategory"
          mandatory
          class="category-chips"
        >
          <v-chip value="all" class="category-chip">
            {{ $t("templateCategories.all") }}
          </v-chip>
          <v-chip value="Umowy o pracę" class="category-chip">
            {{ $t("templateCategories.employment_contracts") }}
          </v-chip>
          <v-chip value="Umowy biznesowe" class="category-chip">
            {{ $t("templateCategories.business_contracts") }}
          </v-chip>
          <v-chip value="Lokale i nieruchomości" class="category-chip">
            {{ $t("templateCategories.real_estate") }}
          </v-chip>
          <v-chip value="Spółki handlowe" class="category-chip">
            {{ $t("templateCategories.companies") }}
          </v-chip>
          <v-chip value="Biznes w sieci" class="category-chip">
            {{ $t("templateCategories.online_business") }}
          </v-chip>
          <v-chip value="Sprawy rodzinne i majątkowe" class="category-chip">
            {{ $t("templateCategories.family_and_property") }}
          </v-chip>
        </v-chip-group>
      </div>
    </div>

    <!-- Templates Grid -->
    <div class="templates-grid">
      <div
        v-for="template in filteredTemplates"
        :key="template.id"
        class="template-card"
        @click="selectTemplate(template)"
      >
        <div class="template-card-content">
          <h3 class="template-title">{{ template.name }}</h3>
          <p class="template-description">{{ template.description }}</p>
          <div class="template-actions">
            <v-btn
              variant="contained"
              class="select-btn"
              :ripple="false"
            >
              {{ $t("common.select") }}
              <v-icon end icon="mdi-arrow-right" />
            </v-btn>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="loading-section">
      <v-progress-circular indeterminate color="primary" size="64" />
      <p class="loading-text">{{ $t("common.loading") }}</p>
    </div>

    <!-- Empty State -->
    <div v-if="!isLoading && filteredTemplates.length === 0" class="empty-state">
      <div class="empty-icon">📄</div>
      <h3 class="empty-title">{{ $t("templates.no_templates_found") }}</h3>
      <p class="empty-description">{{ $t("templates.try_different_search") }}</p>
    </div>

    <!-- AI Assistant Button -->
    <div class="ai-assistant-button">
      <button @click="handleAIAssistant" class="ai-btn">
        <img :src="magicIcon" alt="AI" class="ai-icon" />
        <span>{{ $t("templateSelection.askAI") }}</span>
      </button>
    </div>

    <!-- AI Assistant Component (hidden, controlled externally) -->
    <AIAssistant
      ref="aiAssistantRef"
      buttonStyle="external"
      :currentDocument="null"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import axios from 'axios';
import AIAssistant from '@/components/AIAssistant.vue';
import magicIcon from '@/assets/icons/magic-blue-icon.png';
// Template interface:
// {
//   id: number,
//   name: string,
//   description: string,
//   group: number,
//   category: Category,
//   is_payable: boolean,
//   created_at?: string,
//   updated_at?: string
// }

const router = useRouter();
const store = useStore();
const { t } = useI18n();

// Reactive data
const searchQuery = ref('');
const selectedCategory = ref('all');
const templates = ref([]);
const isLoading = ref(true);
const aiAssistantRef = ref(null);

// Group mappings based on backend dev's message
const categoryGroupMap = {
  'all': null,
  'Umowy o pracę': 1,
  'Spółki handlowe': 2,
  'Biznes w sieci': 3,
  'Umowy biznesowe': 4,
  'Lokale i nieruchomości': 5,
  'Sprawy rodzinne i majątkowe': 6
};

// Helper function to get category name from group number
const getCategoryFromGroup = (groupNumber) => {
  const groupToCategory = {
    1: 'Umowy o pracę',
    2: 'Spółki handlowe',
    3: 'Biznes w sieci',
    4: 'Umowy biznesowe',
    5: 'Lokale i nieruchomości',
    6: 'Sprawy rodzinne i majątkowe'
  };
  return groupToCategory[groupNumber] || 'Inne';
};

// Fetch templates from API
const fetchTemplates = async () => {
  try {
    isLoading.value = true;
    const response = await axios.get('/api/templates', {
      params: {
        page: 1,
        per_page: 100
        // Note: sort: 'group' parameter temporarily removed for testing
      }
    });

    // Store templates with the new group field
    templates.value = response.data.data;

    isLoading.value = false;
  } catch (error) {
    console.error('Error loading templates:', error);
    console.error('Error details:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message
    });
    isLoading.value = false;

    // Set some mock data for development if API is not available
    if (error.response?.status === 500 || error.response?.status === 302) {
      console.warn('Using mock template data for development');
      templates.value = [
        {
          id: 1,
          name: 'Sample Employment Contract',
          description: 'A standard employment contract template',
          group: 1,
          category: { id: 1, name: 'Umowy o pracę' },
          is_payable: true
        },
        {
          id: 2,
          name: 'Business Partnership Agreement',
          description: 'Template for business partnerships',
          group: 4,
          category: { id: 4, name: 'Umowy biznesowe' },
          is_payable: false
        },
        {
          id: 3,
          name: 'Rental Agreement',
          description: 'Property rental contract template',
          group: 5,
          category: { id: 5, name: 'Lokale i nieruchomości' },
          is_payable: true
        }
      ];
    }
  }
};

// Computed properties
const filteredTemplates = computed(() => {
  let filtered = templates.value;

  // Filter by category using group numbers
  if (selectedCategory.value !== 'all') {
    const groupNumber = categoryGroupMap[selectedCategory.value];
    if (groupNumber !== null && groupNumber !== undefined) {
      filtered = filtered.filter(template => template.group === groupNumber);
    } else {
      // Fallback to category name matching for backwards compatibility
      filtered = filtered.filter(template =>
        template.category &&
        (typeof template.category === 'string' ? template.category === selectedCategory.value : template.category.name === selectedCategory.value)
      );
    }
  }

  // Filter by search query
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    filtered = filtered.filter(template =>
      template.name.toLowerCase().includes(query) ||
      template.description.toLowerCase().includes(query)
    );
  }

  return filtered;
});

// Methods
const handleSearch = () => {
  // Search is handled by computed property
};

const selectTemplate = (template) => {
  // Store the selected template
  store.commit('shared/setSelectedTemplate', {
    title: template.name,
    value: template.id,
    template: template
  });

  // Navigate to document questions
  router.push({
    name: 'document-questions',
    query: { template: template.id }
  });
};

const handleAIAssistant = () => {
  if (aiAssistantRef.value) {
    aiAssistantRef.value.openChat();
  }
};

// Lifecycle
onMounted(async () => {
  await fetchTemplates();
});
</script>

<style lang="scss" scoped>
.document-templates-container {
  min-height: 100vh;
  background: #fafafa;
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
  }
}

.header-section {
  max-width: 1200px;
  margin: 0 auto 3rem auto;
}

.page-title {
  font-family: "Archivo", sans-serif;
  font-weight: 700;
  font-size: 48px;
  line-height: 56px;
  color: #1e293b;
  text-align: center;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    font-size: 32px;
    line-height: 40px;
    margin-bottom: 1.5rem;
  }
}

.search-section {
  max-width: 600px;
  margin: 0 auto 2rem auto;

  .search-input {
    font-family: "Archivo", sans-serif;

    ::v-deep .v-field {
      background: white;
      border-radius: 12px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }
  }
}

.filter-section {
  margin-bottom: 2rem;

  .category-chips {
    justify-content: center;
    flex-wrap: wrap;

    .category-chip {
      margin: 0.25rem;
      font-family: "Archivo", sans-serif;
      font-weight: 500;
      background: white !important;
      border: 2px solid #e2e8f0 !important;
      transition: all 0.3s ease;
      color: #374151 !important;

      &:hover {
        border-color: #0023ef !important;
        background: #f8faff !important;
        color: #0019cc !important;
      }

      &.v-chip--selected {
        background: #0023ef !important;
        color: white !important;
        border-color: #0023ef !important;
      }

      // Override any Vuetify defaults
      &.v-chip {
        &.v-chip--selected {
          background: #0023ef !important;
          color: white !important;
          border-color: #0023ef !important;
        }
      }
    }
  }
}

.templates-grid {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
}

.template-card {
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  border: 2px solid transparent;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    border-color: #0023ef;
  }
}

.template-card-content {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.template-title {
  font-family: "Archivo", sans-serif;
  font-weight: 600;
  font-size: 18px;
  line-height: 24px;
  color: #1e293b;
  margin-bottom: 0.75rem;
  min-height: 48px; // Ensure consistent height
}

.template-description {
  font-family: "Archivo", sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: #64748b;
  margin-bottom: 1.5rem;
  flex-grow: 1;
}

.template-actions {
  display: flex;
  justify-content: flex-end;

  .select-btn {
    font-family: "Archivo", sans-serif;
    font-weight: 600;
    text-transform: none;
    border-radius: 100px !important;
    min-width: 120px;
    background-color: #0023ef !important;
    color: white !important;
    border: none !important;

    // Override Vuetify button styles
    &.v-btn {
      border-radius: 100px !important;
    }

    // Additional targeting for Vuetify internal elements
    :deep(.v-btn__content) {
      border-radius: 100px !important;
    }

    :deep(.v-btn__overlay) {
      border-radius: 100px !important;
    }

    &:hover {
      background-color: #0019cc !important;
      color: white !important;
    }

    // Ensure icon is also white
    .v-icon {
      color: white !important;
    }

    &:hover .v-icon {
      color: white !important;
    }
  }
}

.loading-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem;

  .loading-text {
    margin-top: 1rem;
    font-family: "Archivo", sans-serif;
    color: #64748b;
  }
}

.empty-state {
  text-align: center;
  padding: 4rem;

  .empty-icon {
    font-size: 4rem;
    margin-bottom: 1rem;
  }

  .empty-title {
    font-family: "Archivo", sans-serif;
    font-weight: 600;
    font-size: 24px;
    color: #1e293b;
    margin-bottom: 0.5rem;
  }

  .empty-description {
    font-family: "Archivo", sans-serif;
    color: #64748b;
  }
}

.ai-assistant-button {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  z-index: 100;
}

.ai-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 290px;
  height: 54px;
  gap: 8px;
  background: white;
  border: 1px solid rgba(0, 38, 239, 0.3);
  border-radius: 100px;
  padding: 16px 32px;
  font-family: 'Archivo', sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: #0026EF;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  animation: subtleGlow 4s infinite ease-in-out;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(0, 38, 239, 0.25);
    border-color: rgba(0, 38, 239, 0.5);
  }

  &:active {
    transform: translateY(0);
  }

  .ai-icon {
    width: 24px;
    height: 24px;
    transition: transform 0.2s ease;
  }

  &:hover .ai-icon {
    transform: rotate(5deg);
  }
}

@keyframes subtleGlow {
  0%, 100% {
    box-shadow: 0 2px 12px rgba(0, 38, 239, 0.15);
  }
  50% {
    box-shadow: 0 2px 16px rgba(0, 38, 239, 0.25);
  }
}

// Global button styling override
.template-card .v-btn {
  border-radius: 100px !important;
}

// Responsive styles for AI button
@media (max-width: 768px) {
  .ai-assistant-button {
    bottom: 6rem;
    right: 1rem;
    left: 1rem;
    display: flex;
    justify-content: center;
  }

  .ai-btn {
    width: calc(100% - 2rem);
    max-width: 290px;
    font-size: 15px;
    height: 50px;
    padding: 14px 28px;
  }
}

@media (max-width: 480px) {
  .ai-btn {
    font-size: 14px;
    height: 46px;
    padding: 12px 24px;

    .ai-icon {
      width: 20px;
      height: 20px;
    }
  }

  .ai-assistant-button {
    bottom: 7rem;
  }
}
</style>